// src/pages/Performance/EvaluateAppraisal/LeftSidebar.jsx
import React, { useEffect, useMemo } from "react";
import {
  Box,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Chip,
  Divider,
  TextField,
  Paper,
  Checkbox,
  Alert,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import PersonIcon from "@mui/icons-material/Person";
import TimelineIcon from "@mui/icons-material/Timeline";
import SummarizeIcon from "@mui/icons-material/Summarize";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";

import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";

import { GlassCard, ReferenceBadge, AccentBar } from "./evaluateAppraisal.styled";
import {
  formatDate,
  getStepLabel,
  initialsFromName,
  STEP_STATUS_COLOR,
} from "./evaluateAppraisal.utils";

/* ───────────────────────── Small UI helpers ───────────────────────── */
function SectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 950,
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: "block",
            mt: 0.25,
            fontWeight: 600,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function InfoTile({ icon, label, value, tone = "primary" }) {
  const theme = useTheme();
  const pal = theme.palette[tone] || theme.palette.primary;
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.1, md: 1.25 },
        borderRadius: "10px",
        border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
        background:
          theme.palette.mode === "light"
            ? alpha(theme.palette.background.paper, 0.75)
            : alpha(theme.palette.background.paper, 0.55),
        display: "flex",
        gap: 1.2,
        alignItems: "center",
        transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
        "&:hover": {
          borderColor: alpha(pal.main, 0.3),
          boxShadow: `0 8px 20px ${alpha(pal.main, 0.08)}`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          bgcolor: alpha(pal.main, 0.12),
          color: pal.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 800,
            fontSize: "0.62rem",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 850, lineHeight: 1.25, mt: 0.15 }}
          noWrap
          title={value || "-"}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Paper>
  );
}

function SelectRow({ icon, label, checked, disabled, onChange }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.1,
        borderRadius: "10px",
        border: `1px solid ${
          checked
            ? alpha(theme.palette.primary.main, 0.4)
            : alpha(theme.palette.divider, 0.16)
        }`,
        background: checked
          ? `linear-gradient(135deg,
              ${alpha(theme.palette.primary.main, 0.12)} 0%,
              ${alpha(theme.palette.secondary.main, 0.06)} 100%)`
          : alpha(
              theme.palette.background.paper,
              theme.palette.mode === "light" ? 0.72 : 0.52
            ),
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        boxShadow: checked
          ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`
          : "none",
        transition:
          "transform 160ms ease, border-color 160ms ease, background 160ms ease, box-shadow 160ms ease",
        opacity: disabled ? 0.65 : 1,
        "&:hover": {
          borderColor: disabled
            ? alpha(theme.palette.divider, 0.16)
            : alpha(theme.palette.primary.main, 0.32),
          transform: disabled ? "none" : "translateY(-1px)",
        },
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          bgcolor: checked
            ? alpha(theme.palette.primary.main, 0.18)
            : alpha(theme.palette.text.primary, 0.05),
          color: checked ? "primary.main" : "text.secondary",
          transition: "background-color 160ms ease, color 160ms ease",
        }}
      >
        {icon}
      </Avatar>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 850,
          flex: 1,
          color: checked ? "primary.main" : "text.primary",
          transition: "color 160ms ease",
        }}
      >
        {label}
      </Typography>

      <Checkbox checked={checked} onChange={onChange} disabled={disabled} />
    </Paper>
  );
}

export default function LeftSidebar({
  appraisal,
  employee,
  selectedStep,
  noSelectedStep,
  bankCount,
  isEditable,
  isSelfReview,
  referenceInfo,
  prefillInfo,
  overallAutoValue,
  overallAutoCount,
  summaryComment,
  onSummaryChange,
  recommendation,
  onRecChange,
  canShowRecommendations,
  anyRecSelected,
  fieldErrors,
  requirements,
  summaryBlockRef,
  recRemarksBlockRef,
}) {
  const theme = useTheme();

  // Debug: enable via ?perf_rec_debug=1 OR localStorage.setItem("perf_rec_debug","1")
  const debugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const url = new URL(window.location.href);
      const q = url.searchParams.get("perf_rec_debug");
      if (q === "1" || q === "true") return true;
      return localStorage.getItem("perf_rec_debug") === "1";
    } catch {
      return false;
    }
  }, []);

  /**
   * Important fix:
   * LeftSidebar must NOT re-decide visibility using its own viewer/employee id logic.
   * Parent is the ONLY source of truth.
   * If prop is missing/undefined => hide for safety.
   */
  const showRecommendations = Boolean(canShowRecommendations);

  useEffect(() => {
    if (!debugEnabled) return;

    const viewer =
      appraisal?.current_user || appraisal?.auth_user || appraisal?.me || appraisal?.viewer || null;

    const viewerCode =
      viewer?.employee_code ||
      viewer?.employeeCode ||
      viewer?.employee?.employee_code ||
      viewer?.employee?.employeeCode ||
      null;

    const employeeCode =
      appraisal?.employee?.employee_code ||
      appraisal?.employee?.employeeCode ||
      appraisal?.employee_code ||
      appraisal?.employeeCode ||
      employee?.employee_code ||
      employee?.employeeCode ||
      null;

    // eslint-disable-next-line no-console
    console.groupCollapsed(`[PERF_REC][LeftSidebar] appraisal#${appraisal?.id ?? "-"}`);
    // eslint-disable-next-line no-console
    console.table({
      server_is_appraisee_viewer:
        typeof appraisal?.is_appraisee_viewer === "boolean" ? appraisal.is_appraisee_viewer : null,
      canShowRecommendations_prop: canShowRecommendations,
      showRecommendations_effective: showRecommendations,
      viewer_id: viewer?.id ?? null,
      viewer_code: viewerCode ?? null,
      employee_id: appraisal?.employee?.id ?? appraisal?.employee_id ?? employee?.id ?? null,
      employee_user_id: appraisal?.employee?.user_id ?? null,
      employee_code: employeeCode ?? null,
      is_self_review: Boolean(isSelfReview),
      step_id: selectedStep?.id ?? null,
      step_order: selectedStep?.step_order ?? null,
    });
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [debugEnabled, appraisal, employee, canShowRecommendations, showRecommendations, isSelfReview, selectedStep]);

  const currentStepLabel = useMemo(() => getStepLabel(selectedStep), [selectedStep]);
  const currentStepAssignee = selectedStep?.user?.name || "";
  const currentStepAssigneeRole =
    selectedStep?.user?.actual_role ||
    selectedStep?.user?.role ||
    selectedStep?.role_key ||
    selectedStep?.actor_type ||
    "";

  // Red highlight logic shows before submit thanks to requirements.
  const isSummaryError =
    Boolean(fieldErrors?.summary) || (isEditable && Boolean(requirements?.missingSummary));

  const isRecError =
    showRecommendations &&
    (Boolean(fieldErrors?.recommendation_remarks) ||
      (isEditable && Boolean(requirements?.missingRecRemarks)));

  const highlightWrapSx = (active) => ({
    borderRadius: "10px",
    p: 1.15,
    border: `1px solid ${
      active ? alpha(theme.palette.error.main, 0.45) : "transparent"
    }`,
    backgroundColor: active ? alpha(theme.palette.error.main, 0.06) : "transparent",
    transition: "border-color 160ms ease, background-color 160ms ease",
  });

  /* Shared field styling for textareas */
  const fieldSx = (extra = {}) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor:
        theme.palette.mode === "light"
          ? alpha(theme.palette.background.paper, 0.72)
          : alpha(theme.palette.background.paper, 0.45),
      transition: "box-shadow 160ms ease, border-color 160ms ease",
      ...extra,
    },
  });

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 330 },
        flexShrink: 0,
        alignSelf: "flex-start",
        position: { md: "sticky" },
        top: { md: 88 },
      }}
    >
      <Stack spacing={{ xs: 1.25, md: 1.5 }}>
        {/* ───────────── Employee ───────────── */}
        <GlassCard>
          <AccentBar tone="primary" />
          <CardHeader
            sx={{ pb: 1.25, px: { xs: 2, md: 2.5 } }}
            title={<SectionTitle title="Employee" subtitle="Personal & job details" />}
            avatar={
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.18)}`,
                }}
              >
                <PersonIcon />
              </Avatar>
            }
          />
          <CardContent sx={{ pt: 0, px: { xs: 2, md: 2.5 } }}>
            <Stack spacing={1.6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  background:
                    theme.palette.mode === "light"
                      ? `linear-gradient(135deg,
                          ${alpha(theme.palette.primary.main, 0.08)} 0%,
                          ${alpha(theme.palette.background.paper, 0.78)} 60%)`
                      : `linear-gradient(135deg,
                          ${alpha(theme.palette.primary.main, 0.14)} 0%,
                          ${alpha(theme.palette.background.paper, 0.5)} 60%)`,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                      color: "primary.main",
                      fontWeight: 950,
                      fontSize: "1.05rem",
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.22)}`,
                    }}
                  >
                    {initialsFromName(employee?.name || "")}
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 950, lineHeight: 1.15 }}
                      noWrap
                      title={employee?.name || "-"}
                    >
                      {employee?.name || "-"}
                    </Typography>

                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.6, flexWrap: "wrap", gap: 0.5 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<BadgeOutlinedIcon />}
                        label={employee?.employee_code ? `Code: ${employee.employee_code}` : "Code: -"}
                        sx={{ fontWeight: 800, borderRadius: "8px" }}
                      />
                      {employee?.department ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<BusinessOutlinedIcon />}
                          label={employee.department}
                          sx={{ fontWeight: 800, borderRadius: "8px" }}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>

              <Stack spacing={1}>
                <InfoTile
                  icon={<WorkOutlineIcon />}
                  label="Position"
                  value={employee?.position}
                  tone="primary"
                />

                {appraisal?.created_at ? (
                  <InfoTile
                    icon={<EventOutlinedIcon />}
                    label="Created"
                    value={formatDate(appraisal.created_at)}
                    tone="info"
                  />
                ) : null}
              </Stack>
            </Stack>
          </CardContent>
        </GlassCard>

        {/* ───────────── Selected Step ───────────── */}
        <GlassCard>
          <AccentBar tone="info" />
          <CardHeader
            sx={{ pb: 1.25, px: { xs: 2, md: 2.5 } }}
            title={<SectionTitle title="Selected Step" subtitle="Workflow context" />}
            avatar={
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  color: theme.palette.info.main,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.18)}`,
                }}
              >
                <TimelineIcon />
              </Avatar>
            }
          />
          <CardContent sx={{ pt: 0, px: { xs: 2, md: 2.5 } }}>
            {noSelectedStep ? (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "10px",
                  textAlign: "center",
                  border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
                  background: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  No step available.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                    background:
                      theme.palette.mode === "light"
                        ? `linear-gradient(135deg,
                            ${alpha(theme.palette.info.main, 0.07)} 0%,
                            ${alpha(theme.palette.background.paper, 0.78)} 60%)`
                        : alpha(theme.palette.background.paper, 0.52),
                  }}
                >
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          fontWeight: 800,
                          fontSize: "0.62rem",
                        }}
                      >
                        Step
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 950,
                          color: "primary.main",
                          lineHeight: 1.1,
                          mt: 0.35,
                        }}
                        noWrap
                        title={currentStepLabel}
                      >
                        {currentStepLabel}
                      </Typography>
                    </Box>

                    <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.12) }} />

                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          bgcolor: alpha(theme.palette.primary.main, 0.14),
                          color: "primary.main",
                          fontWeight: 950,
                          fontSize: "0.85rem",
                        }}
                      >
                        {initialsFromName(currentStepAssignee)}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            fontWeight: 800,
                            fontSize: "0.62rem",
                          }}
                        >
                          Assignee
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 900, lineHeight: 1.2 }}
                          noWrap
                          title={currentStepAssignee || "-"}
                        >
                          {currentStepAssignee || "-"}
                        </Typography>
                        {currentStepAssigneeRole ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", fontWeight: 600 }}
                            noWrap
                            title={currentStepAssigneeRole}
                          >
                            {currentStepAssigneeRole}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ pt: 0.5, gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={selectedStep?.status || "-"}
                        color={STEP_STATUS_COLOR[selectedStep?.status] || "default"}
                        sx={{ fontWeight: 900, borderRadius: "8px" }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Bank: ${bankCount}`}
                        sx={{ fontWeight: 850, borderRadius: "8px" }}
                      />
                      {isEditable ? (
                        <Chip
                          size="small"
                          color="primary"
                          label="Your Task"
                          icon={<EditIcon />}
                          sx={{ fontWeight: 900, borderRadius: "8px" }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="View Only"
                          icon={<VisibilityIcon />}
                          sx={{ fontWeight: 850, borderRadius: "8px" }}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Paper>

                {referenceInfo?.fromStepOrder ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      borderRadius: "10px",
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      background: alpha(theme.palette.info.main, 0.05),
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                      <ReferenceBadge>
                        <ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" sx={{ fontWeight: 950 }}>
                          {prefillInfo?.wasPrefilled ? "Carried from" : "Reference"} Step #
                          {referenceInfo.fromStepOrder}
                        </Typography>
                      </ReferenceBadge>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.6, fontWeight: 600 }}
                    >
                      {referenceInfo.fromLabel}
                      {referenceInfo.fromAssignee ? ` / ${referenceInfo.fromAssignee}` : ""}
                    </Typography>
                  </Paper>
                ) : null}
              </Stack>
            )}
          </CardContent>
        </GlassCard>

        {/* ───────────── Overall & Summary ───────────── */}
        <GlassCard>
          <AccentBar tone="success" />
          <CardHeader
            sx={{ pb: 1.25, px: { xs: 2, md: 2.5 } }}
            title={<SectionTitle title="Overall & Summary" subtitle="Final notes & recommendation" />}
            avatar={
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.main,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.18)}`,
                }}
              >
                <SummarizeIcon />
              </Avatar>
            }
          />
          <CardContent sx={{ pt: 0, px: { xs: 2, md: 2.5 } }}>
            <Stack spacing={2.2}>
              {!isSelfReview ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: "14px",
                    overflow: "hidden",
                    position: "relative",
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    background:
                      theme.palette.mode === "light"
                        ? `linear-gradient(135deg,
                            ${alpha(theme.palette.success.main, 0.12)} 0%,
                            ${alpha(theme.palette.primary.main, 0.06)} 55%,
                            ${alpha(theme.palette.background.paper, 0.7)} 100%)`
                        : `linear-gradient(135deg,
                            ${alpha(theme.palette.success.main, 0.16)} 0%,
                            ${alpha(theme.palette.background.paper, 0.5)} 60%)`,
                  }}
                >
                  {/* Decorative glow */}
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      top: -30,
                      right: -30,
                      width: 110,
                      height: 110,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${alpha(
                        theme.palette.success.main,
                        0.18
                      )} 0%, transparent 70%)`,
                      pointerEvents: "none",
                    }}
                  />

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ position: "relative" }}
                  >
                    <AutoAwesomeOutlinedIcon
                      sx={{ fontSize: 16, color: theme.palette.success.main }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontWeight: 850,
                        fontSize: "0.62rem",
                      }}
                    >
                      Overall Evaluation (auto)
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="baseline"
                    sx={{ mt: 0.6, position: "relative" }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 980,
                          color: "primary.main",
                          lineHeight: 0.9,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {overallAutoValue || "-"}
                      </Typography>
                      {overallAutoValue ? (
                        <StarRoundedIcon
                          sx={{
                            fontSize: 22,
                            color: theme.palette.warning.main,
                            mb: 1.5,
                          }}
                        />
                      ) : null}
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Based on {overallAutoCount} rated question(s) / excludes NO
                    </Typography>
                  </Stack>
                </Paper>
              ) : null}

              <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.12) }} />

              {/* Summary */}
              <Box ref={summaryBlockRef} sx={highlightWrapSx(isSummaryError)}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.62rem",
                    }}
                  >
                    {isSelfReview ? "Self review summary" : "Summary / strengths & areas to improve"}
                  </Typography>

                  {isEditable ? (
                    <Tooltip title={isSummaryError ? "This field is required" : "Required"}>
                      <Chip
                        size="small"
                        label="Required"
                        color={isSummaryError ? "error" : "default"}
                        variant="outlined"
                        sx={{ borderRadius: "8px", fontWeight: 950, flexShrink: 0 }}
                      />
                    </Tooltip>
                  ) : null}
                </Stack>

                {isEditable && isSummaryError ? (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                      mt: 1,
                      borderRadius: "10px",
                      py: 0.5,
                      px: 1.1,
                      fontWeight: 850,
                      borderColor: alpha(theme.palette.error.main, 0.35),
                      backgroundColor: alpha(theme.palette.error.main, 0.04),
                      "& .MuiAlert-message": { width: "100%" },
                    }}
                  >
                    Please fill the required field below.
                  </Alert>
                ) : null}

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={summaryComment}
                  onChange={(e) => onSummaryChange(e.target.value)}
                  disabled={!isEditable}
                  placeholder={
                    isSelfReview
                      ? "Write your self review comments..."
                      : "Write a clear summary with strengths and areas for improvement..."
                  }
                  error={isSummaryError}
                  helperText={
                    fieldErrors?.summary ||
                    (isEditable
                      ? isSelfReview
                        ? "Required: add at least one comment (summary or any question comment)."
                        : "Required."
                      : " ")
                  }
                  sx={{
                    mt: 0.9,
                    ...fieldSx(
                      isEditable
                        ? {
                            "&:hover": {
                              boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.08)}`,
                            },
                          }
                        : {}
                    ),
                  }}
                />
              </Box>

              {/* Recommendation visibility is controlled only by the parent prop. */}
              {showRecommendations ? (
                <>
                  <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.12) }} />

                  <Box ref={recRemarksBlockRef} sx={highlightWrapSx(isRecError)}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <EmojiEventsOutlinedIcon
                          sx={{ fontSize: 18, color: theme.palette.primary.main }}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>
                          Recommendation
                        </Typography>
                      </Stack>

                      {anyRecSelected ? (
                        <Chip
                          size="small"
                          color={isRecError ? "error" : "primary"}
                          variant="outlined"
                          label="Remarks required"
                          sx={{ fontWeight: 900, borderRadius: "8px", flexShrink: 0 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Optional"
                          sx={{ fontWeight: 850, borderRadius: "8px", flexShrink: 0 }}
                        />
                      )}
                    </Stack>

                    {isEditable && isRecError ? (
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{
                          mt: 1,
                          borderRadius: "10px",
                          py: 0.5,
                          px: 1.1,
                          fontWeight: 850,
                          borderColor: alpha(theme.palette.error.main, 0.35),
                          backgroundColor: alpha(theme.palette.error.main, 0.04),
                          "& .MuiAlert-message": { width: "100%" },
                        }}
                      >
                        Please add remarks for the selected recommendation.
                      </Alert>
                    ) : null}

                    <Stack spacing={1.1} sx={{ mt: 1.1 }}>
                      <SelectRow
                        icon={<TrendingUpOutlinedIcon />}
                        label="Promotion"
                        checked={!!recommendation?.promotion}
                        disabled={!isEditable}
                        onChange={(e) => onRecChange("promotion", e.target.checked)}
                      />
                      <SelectRow
                        icon={<PaidOutlinedIcon />}
                        label="Increment"
                        checked={!!recommendation?.increment}
                        disabled={!isEditable}
                        onChange={(e) => onRecChange("increment", e.target.checked)}
                      />
                      <SelectRow
                        icon={<EmojiEventsOutlinedIcon />}
                        label="Bonus"
                        checked={!!recommendation?.bonus}
                        disabled={!isEditable}
                        onChange={(e) => onRecChange("bonus", e.target.checked)}
                      />

                      <TextField
                        label={`Recommendation Remarks${anyRecSelected ? " *" : ""}`}
                        fullWidth
                        multiline
                        minRows={3}
                        value={recommendation?.remarks || ""}
                        onChange={(e) => onRecChange("remarks", e.target.value)}
                        disabled={!isEditable}
                        required={!!anyRecSelected}
                        error={isRecError}
                        helperText={
                          fieldErrors?.recommendation_remarks ||
                          (isEditable && anyRecSelected
                            ? "Required when a recommendation is selected."
                            : " ")
                        }
                        placeholder="Write recommendation remarks..."
                        sx={fieldSx()}
                      />
                    </Stack>
                  </Box>
                </>
              ) : null}

              {!isEditable ? (
                <Alert
                  severity="info"
                  icon={<VisibilityIcon fontSize="inherit" />}
                  sx={{
                    borderRadius: "10px",
                    fontWeight: 700,
                    backgroundColor: alpha(theme.palette.info.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                  }}
                >
                  You are viewing a step that is not your current pending task (read-only).
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </GlassCard>
      </Stack>
    </Box>
  );
}
